// Локальные настройки Mini App (HQ-1059): то, что человек выбирает в Профиле и что не уходит на сервер — оформление
// (System / Light / Dark) и видеоблоки (строка меню toggle с setting `video_blocks`). Храним в localStorage; недоступен
// (приватный режим) — выбор живёт до перезапуска. Подписчики узнают о смене сразу: тема перекрашивается без перезагрузки,
// видеоблоки исчезают с открытых экранов.
import { useSyncExternalStore } from 'react';

/** Оформление: system — схема Telegram (colorScheme), light / dark — своя поверх неё. */
export type Appearance = 'system' | 'light' | 'dark';
export const APPEARANCES: readonly Appearance[] = ['system', 'light', 'dark'];

const APPEARANCE_KEY = 'cricket.appearance';
const VIDEO_BLOCKS_KEY = 'cricket.video_blocks';

function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function write(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* приватный режим — до перезапуска */ }
}

const parseAppearance = (raw: string | null): Appearance =>
  (APPEARANCES as readonly string[]).includes(raw ?? '') ? (raw as Appearance) : 'system';

let appearance: Appearance = parseAppearance(read(APPEARANCE_KEY));
// по умолчанию видеоблоки включены: выключает только явное «0»
let videoBlocks = read(VIDEO_BLOCKS_KEY) !== '0';
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((cb) => cb());

/** Подписка на смену любой локальной настройки. Возвращает отписку. */
export function onLocalSettingsChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export const getAppearance = (): Appearance => appearance;

export function setAppearance(next: Appearance): void {
  if (next === appearance) return;
  appearance = next;
  write(APPEARANCE_KEY, next);
  emit();
}

/** Схема экрана: выбор человека, а для system — схема Telegram. */
export const schemeFor = (telegramScheme: 'light' | 'dark'): 'light' | 'dark' =>
  (appearance === 'system' ? telegramScheme : appearance);

export const getVideoBlocks = (): boolean => videoBlocks;

export function setVideoBlocks(on: boolean): void {
  if (on === videoBlocks) return;
  videoBlocks = on;
  write(VIDEO_BLOCKS_KEY, on ? '1' : '0');
  emit();
}

export const useAppearance = (): Appearance => useSyncExternalStore(onLocalSettingsChange, getAppearance, getAppearance);
export const useVideoBlocks = (): boolean => useSyncExternalStore(onLocalSettingsChange, getVideoBlocks, getVideoBlocks);
